import classNames from 'classnames/bind';
import Toast, { useToast } from 'common/searchBox/Toast';
import SelectSeat from 'common/searchBox/selectSeat';
import { DateFormat } from 'common/types';
import { days } from 'common/utils';
import { useInfoParamsContext } from 'contexts/infoParams';
import { Modal, ModalFuncContext, ModalPosition } from 'contexts/modalFunc';
import {
  ICity,
  IPassengers,
  SearchType,
  SeatClass,
  TripType,
  getLinkPair,
  getPassengerDesc,
  getSeatDesc,
  useSearchParamsContext,
} from 'contexts/searchParams';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import { areaCode, mobileDeviceWidth } from '../utilChart';
import ChartBar from './chartBar';
import styles from './index.module.scss';
import TopInfoBox from './info';

const cx = classNames.bind(styles);

function getPassengerSeatDesc(passengers: IPassengers, seatType: string) {
  return `${getPassengerDesc(passengers)}, ${getSeatDesc(false, seatType)}`;
}

const getDepartureDate = (depDate: string, isProductChange?: boolean) => {
  if (depDate) {
    return days(depDate).toDate();
  }
  // 캘린더+모바일+출발날짜 없을때
  if (!isProductChange && window?.innerWidth < mobileDeviceWidth) {
    return days(new Date()).add(1, 'day').toDate();
  }
  return null; // 캘린더+PC+출발날짜 없을때
};

function getMakeItinerary(
  tripType: TripType,
  departureCity: ICity,
  arrivalCity: ICity,
  departureDate: Date | undefined,
  period: number,
) {
  let itineraryList = [
    {
      departureCity: { ...departureCity, isDomestic: true },
      arrivalCity: { ...arrivalCity, isDomestic: false },
      departureDate: days(departureDate).format(DateFormat.YMD),
    },
  ];
  // 편도
  if (tripType === TripType.OW) return itineraryList;
  // 왕복
  return [
    ...itineraryList,
    {
      departureCity: { ...arrivalCity, isDomestic: false },
      arrivalCity: { ...departureCity, isDomestic: true },
      departureDate: days(departureDate)
        .add(period - 1, 'day')
        .format(DateFormat.YMD),
    },
  ];
}

interface IProps {
  payload?: {
    isPopular: boolean;
    tripType: TripType;
    seatClass: SeatClass;
  };
}

const ChartOversea = (props?: IProps) => {
  const {
    searchParams,
    searchParams: {
      passengers: passengersCtx = {
        adult: 1,
        child: 0,
        infant: 0,
      },
      isDirect: isDirectCtx = false,
    },
  } = useSearchParamsContext();
  const {
    infoSearchParams,
    infoSearchParams: {
      departureCity,
      arrivalCity,
      period,
      departureDate: paramDepartureDate,
    },
  } = useInfoParamsContext();
  let { tripType = TripType.RT, seatClass: seatClassCtx = SeatClass.Y } =
    searchParams;
  let { isProductChange } = infoSearchParams;
  // 메인 인기여행지 선택시
  if (props?.payload?.isPopular) {
    const { payload } = props;
    tripType = payload?.tripType;
    seatClassCtx = payload?.seatClass;
    isProductChange = false;
  }

  const router = useRouter();
  const [message, showToast] = useToast();
  const setModal = useContext(ModalFuncContext);
  const [showSeatOption, toggleSeatOption] = useState(false);
  const [departureDate, setDepartureDate] = useState<Date | null>(
    getDepartureDate(paramDepartureDate, isProductChange),
  );
  const [passengers, setPassengerState] = useState(passengersCtx);
  const [seatClass, setSeatClassState] = useState(seatClassCtx);
  const [isDirect, toggleDirect] = useState(isDirectCtx);
  const setPassengers = (personType: string, diff: number) => {
    const newPassengers = {
      ...passengers,
      [personType]: passengers[personType] + diff,
    };
    if (newPassengers.adult < newPassengers.infant) {
      newPassengers.infant = newPassengers.adult;
    }
    setPassengerState(newPassengers);
  };

  const setSeatClass = (sc: SeatClass) => {
    setSeatClassState(sc);
  };

  const searchFlights = () => {
    if (!departureDate) {
      showToast('날짜를 선택해주세요.');
      return;
    }

    const itineraryList = getMakeItinerary(
      tripType,
      { ...departureCity, isDomestic: true },
      { ...arrivalCity, isDomestic: false },
      departureDate,
      period,
    );
    const [href, as] = getLinkPair(
      itineraryList,
      passengers,
      seatClass,
      isDirect,
      SearchType.INTERNATIONAL,
    );
    setModal({ type: Modal.NULL, position: ModalPosition.MAIN });
    router.push(href, as, { shallow: false });
  };

  useEffect(() => {
    if (infoSearchParams?.isProductChange) {
      setDepartureDate(days(infoSearchParams?.departureDate).toDate());
    }
  }, [infoSearchParams]);

  return (
    <div className={cx('chart')}>
      <TopInfoBox
        position="grp"
        isDirect={isDirect}
        toggleDirect={toggleDirect}
      />
      <ChartBar
        trip={tripType} // OW, RT만 해당. MD 는 없음
        refresh={false} // refresh true/false
        onSelectedDate={setDepartureDate}
      />
      <div className={cx('chart_query')}>
        <div className={cx('conditions')}>
          <button
            type="button"
            className={cx('condition', 'as_seat')}
            aria-expanded={showSeatOption}
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.passeat`}
            onClick={() => toggleSeatOption(!showSeatOption)}
          >
            {getPassengerSeatDesc(passengers, seatClass)}
          </button>
          <button
            type="button"
            className={cx('condition', 'as_direct')}
            aria-checked={isDirect}
            data-event-nlog="click"
            data-event-area-code={`${areaCode}.${!isDirect ? 'direct' : 'directcuc'}`}
            onClick={() => toggleDirect(!isDirect)}
          >
            직항만
          </button>
          {showSeatOption && (
            <div className={cx('select_seat')}>
              <SelectSeat
                isDomestic={false}
                passengers={passengers}
                seatClass={seatClass}
                tripType={tripType}
                setPassengers={setPassengers}
                setSeatClass={setSeatClass}
                toggleSeatOption={toggleSeatOption}
              />
            </div>
          )}
        </div>
        <button
          type="button"
          className={cx('search')}
          data-event-nlog="click"
          data-event-area-code={`${areaCode}.search`}
          onClick={searchFlights}
        >
          항공권 검색
        </button>
      </div>
      <Toast message={message} />
    </div>
  );
};

export default ChartOversea;
